import { GameLoop } from "../core/loop";
import { Input } from "../core/input";
import { Renderer } from "../render/renderer";
import { GameState } from "./state";
import { maxIntegrity } from "./stats";
import { Rng, seedFromString } from "../core/rng";

import { updatePlayer } from "./systems/player";
import { updateEnemies } from "./systems/ai";
import { updateWeapons, fireManualShot } from "./systems/weapons";
import { updateProjectiles } from "./systems/projectiles";
import { updatePickups } from "./systems/pickups";
import { updateFx } from "./systems/fx";
import { resetSpawner, updateSpawner, makeEnemy } from "./systems/spawner";
import { updateBoss } from "./systems/boss";

import { applyUpgrade, rollChoices } from "./upgrades";
import {
  drawHud,
  drawUpgrades,
  drawBossBar,
  drawTitle,
  drawLevelUp,
  drawPause,
  drawGameOver,
  drawStageIntro,
  drawSurgeDial,
  drawLab,
  labHitRects,
  levelUpCardRects,
  HUD_BAR,
} from "../ui/hud";
import { STAGES } from "../data/stages";
import { loadMeta, applyMeta, bankRun, buyMeta, META_UPGRADES } from "../ui/meta";
import { TAU } from "../core/math";
import { Music } from "../audio/music";
import { regionName } from "../data/regions";
import {
  BOSSES,
  bossEnemyDef,
  bossPlanForZone,
  miniBossById,
  stageBossHpScale,
  miniBossHpScale,
  zeroDayEncounterIndex,
  abilitiesForBoss,
  stageIndexForZone,
  PURGE_BOSS_DAMAGE_FRACTION,
  type BossPlan,
} from "../data/bosses";
import { submitScore as postScore, getName, bumpPrestige, type ScoreEntry } from "../ui/leaderboard";
import { loadSettings, saveSettings, type Settings } from "../ui/settings";
import { difficultyAt } from "../data/difficulty";
import { titleHitRects } from "../ui/hud";

// Top-level coordinator: owns the loop, renderer, input, and the active run.
// Drives the phase state machine and dispatches the simulation systems.
export class Game {
  private renderer: Renderer;
  private input: Input;
  private loop: GameLoop;
  private state: GameState;
  private seed: number;
  private renderClock = 0;
  private music = new Music();
  private allDownFor = 0; // seconds all nodes have been offline (loss grace)
  private levelupGuard = 0; // brief input lock when an upgrade screen opens
  private leaderboard: ScoreEntry[] = [];
  private scoreSubmitted = false;
  private stageIntroGuard = 0; // brief lock so the kill tap doesn't skip the story card
  private isTouch = "ontouchstart" in window || navigator.maxTouchPoints > 0;
  private ultBtn = document.getElementById("btn-ultimate") as HTMLElement | null;
  private settings: Settings = loadSettings();
  private meta = loadMeta();
  private labSel = 0;

  constructor(canvas: HTMLCanvasElement) {
    this.renderer = new Renderer(canvas);
    this.input = new Input(window, canvas);
    this.seed = pickSeed();
    const a = this.arenaSize();
    this.state = new GameState(this.seed, a.w, a.h);
    this.state.phase = "title";
    this.loop = new GameLoop(
      (dt) => this.update(dt),
      (alpha) => this.render(alpha),
      60,
    );

    // Unlock/resume audio from a REAL user gesture. The start tap is consumed a
    // frame later in the loop, which mobile browsers don't count as a gesture,
    // so the context would stay suspended (no music on mobile). Resuming here,
    // synchronously inside the gesture, fixes that and re-resumes after the OS
    // suspends audio on backgrounding.
    const unlockAudio = (): void => this.music.unlock();
    for (const ev of ["pointerdown", "touchend", "keydown"]) {
      window.addEventListener(ev, unlockAudio, { passive: true });
    }
  }

  start(): void {
    this.loop.start();
  }

  /** World size that matches the play area aspect so the board fills it. */
  private arenaSize(): { w: number; h: number } {
    const playW = Math.max(320, this.renderer.viewW);
    const playH = Math.max(240, this.renderer.viewH - HUD_BAR);
    const baseH = 1000;
    return { w: Math.round(baseH * (playW / playH)), h: baseH };
  }

  private newRun(): void {
    // Fresh seed each redeploy unless one was pinned via ?seed=.
    this.seed = pickSeed();
    resetSpawner();
    this.allDownFor = 0;
    this.scoreSubmitted = false;
    this.leaderboard = [];
    const a = this.arenaSize();
    this.state = new GameState(this.seed, a.w, a.h);
    // Difficulty selection is held for now — every run uses the baseline tier.
    this.state.difficulty = difficultyAt(1); // MAINTENANCE (normal)
    this.state.screenShake = this.settings.screenShake;
    this.state.reduceFlashing = this.settings.reduceFlashing;
    // Apply permanent meta-upgrades bought in the Lab, then top up to new max.
    applyMeta(this.state.stats, this.meta);
    this.state.player.integrity = maxIntegrity(this.state.stats);
    this.showStageIntro(0); // open with the Stage I story card
    // confirm/Enter is a user gesture, so audio is allowed to start here.
    void this.music.start();
  }

  /** Upgrade Lab: spend banked fragments on permanent meta-upgrades. */
  private updateLab(): void {
    const { rows, back } = labHitRects(this.renderer.viewW, this.renderer.viewH);
    const n = META_UPGRADES.length;
    if (this.input.navY !== 0) this.labSel = (this.labSel + this.input.navY + n) % n;

    let buyIndex = this.input.menuConfirm ? this.labSel : -1;
    let goBack = this.input.pause;
    for (const tap of this.input.frameTaps) {
      if (inRect(tap, back)) {
        goBack = true;
        break;
      }
      const hit = rows.findIndex((r) => inRect(tap, r));
      if (hit >= 0) {
        this.labSel = hit;
        buyIndex = hit;
        break;
      }
    }
    if (buyIndex >= 0) buyMeta(this.meta, META_UPGRADES[buyIndex]);
    if (goBack) this.state.phase = "title";
  }

  private showStageIntro(idx: number): void {
    this.state.stageIntroIndex = idx;
    this.state.phase = "stageintro";
    this.stageIntroGuard = 0.4;
  }

  private update(dt: number): void {
    this.input.poll();
    const s = this.state;

    // Mute toggle works in any phase.
    if (this.input.mute) this.music.toggleMute();

    const tapped = this.input.frameTaps.length > 0;

    switch (s.phase) {
      case "title":
        this.updateTitle();
        break;

      case "playing":
        this.simulate(dt);
        if (this.input.pause) s.phase = "paused";
        // Leveling is independent of zones: present a choose-3 on each level-up.
        if (s.pendingLevelUps > 0) this.enterLevelUp();
        break;

      case "levelup":
        this.updateLevelUp(dt);
        break;

      case "paused":
        // Any tap resumes; on-screen pause button also toggles via input.pause.
        if (this.input.pause || this.input.confirm || tapped) s.phase = "playing";
        break;

      case "gameover":
        if (!this.scoreSubmitted) {
          this.scoreSubmitted = true;
          bankRun(this.meta, s.fragments, s.zone); // fragments persist into the Lab
          void this.submitRunScore();
        }
        if (this.input.confirm || tapped) s.phase = "title";
        break;

      case "stageintro":
        if (this.stageIntroGuard > 0) this.stageIntroGuard -= dt;
        else if (this.input.confirm || tapped) s.phase = "playing";
        break;

      case "lab":
        this.updateLab();
        break;
    }

    this.updateMusicScene();
  }

  /**
   * Drive the adaptive soundtrack from game state: zen on menus, a build that
   * intensifies as the zone timer drops toward zero, and an intense boss theme.
   */
  private updateMusicScene(): void {
    const s = this.state;
    if (s.phase === "title" || s.phase === "gameover" || s.phase === "stageintro" || s.phase === "lab") {
      this.music.setScene("menu", 0.25);
      return;
    }
    const boss = s.bossEnemy;
    if (boss && boss.alive) {
      // Intensity climbs as the boss is worn down.
      const hpFrac = boss.maxHp > 0 ? boss.hp / boss.maxHp : 1;
      this.music.setScene("boss", 0.8 + (1 - hpFrac) * 0.2);
      return;
    }
    // Survival: dominated by how close the defend timer is to zero.
    const timerFrac = s.zoneDuration > 0 ? 1 - Math.max(0, Math.min(1, s.zoneTimer / s.zoneDuration)) : 0;
    let alive = 0;
    for (const e of s.enemies) if (e.alive) alive++;
    const pressure = Math.min(1, alive / 60);
    let intensity = 0.12 + 0.88 * Math.max(timerFrac, pressure * 0.5);
    if (s.phase !== "playing") intensity *= 0.55; // calmer while choosing an upgrade / paused
    this.music.setScene("play", intensity);
  }

  private simulate(dt: number): void {
    const s = this.state;
    s.time += dt;
    s.firedThisFrame = false;

    updatePlayer(s, dt, this.input);
    updateSpawner(s, dt);
    updateWeapons(s, dt);

    // Manual fire: one bolt per Space/RT press at the nearest target.
    if (this.input.shoot) fireManualShot(s);

    // Shoot SFX (throttled in the audio engine) when any weapon fired this tick.
    if (s.firedThisFrame) this.music.sfxShoot();

    // Signal ultimate — Communication Purge (the network as a weapon): when the
    // Signal meter is full, clear the board and restore every node.
    if (this.input.ultimate && s.signal >= s.signalMax) this.purge();
    updateEnemies(s, dt);
    updateBoss(s, dt);
    updateProjectiles(s, dt);
    updatePickups(s, dt);
    updateFx(s, dt);

    // Zone / boss progression: defend until the timer ends. Most zones are pure
    // survival — surviving restores the region. Only mini-boss / stage-boss
    // zones summon a boss that must be defeated before the region is restored.
    if (!s.bossEnemy) {
      s.zoneTimer -= dt;
      if (s.zoneTimer <= 0) this.onZoneTimerEnd();
    } else if (!s.bossEnemy.alive) {
      this.onBossDefeated();
    }

    // Fail: if every node stays offline past a short grace, the network collapses.
    const allDown = s.nodes.length > 0 && s.nodes.every((n) => !n.online);
    this.allDownFor = allDown ? this.allDownFor + dt : 0;
    if (this.allDownFor > 2.5) s.phase = "gameover";
    // (Soundtrack scene is updated centrally in updateMusicScene, every frame.)
  }

  /** Title screen: difficulty select + accessibility toggles, then start. */
  private updateTitle(): void {
    const r = titleHitRects(this.renderer.viewW, this.renderer.viewH);
    let start = this.input.confirm;
    let tShake = this.input.toggleShake;
    let tFlash = this.input.toggleFlash;
    let openLab = false;
    for (const tap of this.input.frameTaps) {
      if (inRect(tap, r.shake)) tShake = true;
      else if (inRect(tap, r.flash)) tFlash = true;
      else if (inRect(tap, r.lab)) openLab = true;
      else start = true; // tapping START (or empty space) begins the run
    }
    if (openLab) {
      this.labSel = 0;
      this.state.phase = "lab";
      return;
    }
    if (tShake) {
      this.settings.screenShake = !this.settings.screenShake;
      saveSettings(this.settings);
    }
    if (tFlash) {
      this.settings.reduceFlashing = !this.settings.reduceFlashing;
      saveSettings(this.settings);
    }
    if (start) this.newRun();
  }

  private enterLevelUp(): void {
    const s = this.state;
    s.pendingLevelUps--;
    s.pendingChoices = rollChoices(s, 3);
    s.selIndex = 0;
    s.phase = "levelup";
    this.levelupGuard = 0.3; // ignore input briefly so in-flight taps don't auto-pick
  }

  private updateLevelUp(dt: number): void {
    const s = this.state;
    const n = s.pendingChoices.length;
    if (n === 0) {
      s.phase = "playing";
      return;
    }
    if (this.levelupGuard > 0) {
      this.levelupGuard -= dt;
      return;
    }
    if (this.input.navX !== 0) {
      s.selIndex = (s.selIndex + this.input.navX + n) % n;
    }

    // Number keys 1..n pick directly; Enter/A confirms (NOT Space — that's fire).
    let pickIndex = -1;
    if (this.input.digit >= 1 && this.input.digit <= n) pickIndex = this.input.digit - 1;
    else if (this.input.menuConfirm) pickIndex = s.selIndex;

    // Touch: tap a card to choose it directly.
    if (pickIndex < 0 && this.input.frameTaps.length > 0) {
      const { rects } = levelUpCardRects(n, this.renderer.viewW, this.renderer.viewH);
      for (const tap of this.input.frameTaps) {
        const hit = rects.findIndex(
          (r) => tap.x >= r.x && tap.x <= r.x + r.w && tap.y >= r.y && tap.y <= r.y + r.h,
        );
        if (hit >= 0) {
          pickIndex = hit;
          break;
        }
      }
    }

    if (pickIndex >= 0) {
      applyUpgrade(s, s.pendingChoices[pickIndex]);
      s.pendingChoices = [];
      s.phase = s.pendingLevelUps > 0 ? "levelup" : "playing";
      if (s.phase === "levelup") this.enterLevelUp();
    }
  }

  private advanceZone(): void {
    const s = this.state;
    s.addFloat(s.player.x, s.player.y - 90, `ZONE ${s.zone} RESTORED`, "#8cc85f", 26);
    s.zone++;
    this.allDownFor = 0;
    resetSpawner();
    s.startZone(); // new random node layout, board cleared, PCKT recentered & repaired
    s.addFloat(s.player.x, s.player.y - 60, `ZONE ${s.zone} — ${regionName(s.zone).toUpperCase()}`, "#80a0ff", 28);
  }

  /** Defend timer expired: survival zones restore directly; boss zones summon. */
  private onZoneTimerEnd(): void {
    const s = this.state;
    const plan = bossPlanForZone(s.zone, s.storyComplete);
    if (plan.kind === "survival") {
      this.advanceZone(); // pure tower-defense zone: survived → region restored
      return;
    }
    this.spawnBoss(plan);
  }

  /** Spawn the boss for a mini-boss or stage-boss zone. */
  private spawnBoss(plan: BossPlan): void {
    const s = this.state;
    s.enemies = []; // clear the trickle for a clean encounter
    s.zoneTimer = 0;
    s.addShake(10);
    if (plan.kind === "mini") {
      this.spawnMiniBoss(plan.bossId!);
    } else if (plan.glitched) {
      this.spawnGlitchedStage(plan);
    } else {
      this.spawnStageBoss(plan.bossId!);
    }
  }

  private spawnMiniBoss(id: string): void {
    const s = this.state;
    const def = miniBossById(id)!;
    const e = makeEnemy(bossEnemyDef(def), s.arenaW / 2, s.arenaH * 0.25, miniBossHpScale(s.zone) * s.difficulty.bossHp);
    e.bossState = {
      name: def.name,
      abilities: def.abilities,
      timer: 1.6,
      index: 0,
      isZeroDay: false,
      isMini: true,
    };
    s.enemies.push(e);
    s.bossEnemy = e;
    s.addFloat(s.player.x, s.player.y - 90, `MINI-BOSS: ${def.name.toUpperCase()}`, "#e3c78a", 24);
  }

  private spawnStageBoss(id: string): void {
    const s = this.state;
    const def = BOSSES[id];
    const e = makeEnemy(bossEnemyDef(def), s.arenaW / 2, s.arenaH * 0.25, stageBossHpScale(s.zone) * s.difficulty.bossHp);
    const isZero = id === "zero_day";
    e.bossState = {
      name: def.name,
      abilities: abilitiesForBoss(id, s.rng, zeroDayEncounterIndex(s.zone)),
      timer: 1.4,
      index: 0,
      isZeroDay: isZero,
    };
    s.enemies.push(e);
    s.bossEnemy = e;
    if (isZero) {
      s.addFloat(s.player.x, s.player.y - 100, "UNKNOWN THREAT — ZERO DAY", "#cf87e8", 28);
      s.addFloat(s.player.x, s.player.y - 72, "NO COUNTERMEASURE. ADAPT.", "#cf87e8", 16);
    } else {
      s.addFloat(s.player.x, s.player.y - 90, `STAGE BOSS: ${def.name.toUpperCase()}`, "#ff5454", 26);
    }
  }

  /**
   * Glitched mode: the Day boss PCKT expects appears, but the Firewall arrives
   * and destroys it (handled in the boss system once the intro timer ends),
   * then turns on PCKT. Here we just stage the doomed Day boss.
   */
  private spawnGlitchedStage(plan: BossPlan): void {
    const s = this.state;
    const def = BOSSES[plan.expectedBossId!];
    const e = makeEnemy(bossEnemyDef(def), s.arenaW / 2, s.arenaH * 0.22, stageBossHpScale(s.zone) * s.difficulty.bossHp);
    e.bossState = {
      name: def.name,
      abilities: [],
      timer: 999,
      index: 0,
      isZeroDay: false,
      invulnerable: true,
      scripted: "glitched_intro",
      scriptTimer: 2.6,
    };
    s.enemies.push(e);
    s.bossEnemy = e;
    s.addFloat(s.player.x, s.player.y - 100, `INBOUND: ${def.name.toUpperCase()}`, "#ff5454", 24);
  }

  /** Boss down → region restored; Zero Day's first fall reveals the truth. */
  private onBossDefeated(): void {
    const s = this.state;
    const bs = s.bossEnemy?.bossState;
    const wasStageBoss = !!bs && !bs.isMini; // stage boss / Zero Day (not a mini)
    if (bs?.isZeroDay) {
      bumpPrestige();
      if (!s.storyComplete) {
        s.storyComplete = true;
        s.addFloat(s.player.x, s.player.y - 110, "ZERO DAY CONTAINED", "#8cc85f", 28);
        s.addFloat(s.player.x, s.player.y - 82, "THE MINIONS WERE ITS PROTOTYPES", "#cf87e8", 16);
      } else {
        s.addFloat(s.player.x, s.player.y - 100, "ZERO DAY CONTAINED — IT WILL RETURN", "#8cc85f", 22);
      }
    } else if (bs?.glitchedFirewall) {
      s.addFloat(s.player.x, s.player.y - 100, "FIREWALL FREED FROM CORRUPTION", "#74b2ff", 22);
    }
    this.advanceZone();
    // Entering a new story stage → show its narrative card.
    if (wasStageBoss) {
      const idx = stageIndexForZone(s.zone);
      if (idx < STAGES.length) this.showStageIntro(idx);
    }
  }

  private async submitRunScore(): Promise<void> {
    const s = this.state;
    this.leaderboard = await postScore({
      name: getName(),
      zone: s.zone,
      time: s.time,
      level: s.level,
      kills: s.kills,
      fragments: s.fragments,
    });
  }

  /**
   * Surge / Communication Purge (R): channel restored communication as a weapon. It
   * vaporizes weak enemies outright, but bosses and mini-bosses only take a
   * fraction of their max HP (PURGE_BOSS_DAMAGE_FRACTION) — it can't delete a
   * boss fight, only dent it. It does NOT repair nodes: a downed node is only
   * rebuilt by PCKT sitting in it.
   */
  private purge(): void {
    const s = this.state;
    s.signal = 0;
    s.addShake(14);
    this.music.sfxSurge();
    s.ring(s.player.x, s.player.y, "#cf87e8", 52, 460);
    s.addFloat(s.player.x, s.player.y - 80, "COMMUNICATION PURGE", "#cf87e8", 26);
    for (const e of s.enemies) {
      if (!e.alive) continue;
      if (e.bossState) {
        if (e.bossState.invulnerable) continue; // scripted-intro boss is immune
        s.damageEnemy(e, e.maxHp * PURGE_BOSS_DAMAGE_FRACTION, false);
        s.addFloat(e.x, e.y - e.radius - 6, "PURGE RESISTED", "#cf87e8", 14);
      } else {
        s.killEnemy(e, false); // weak corruption: instakill (no Signal refund)
      }
    }
  }

  /** Fill the on-screen SURGE button bottom-up to show the Signal recharge. */
  private updateUltButton(s: GameState): void {
    if (!this.ultBtn) return;
    const frac = Math.max(0, Math.min(1, s.signal / s.signalMax));
    const ready = frac >= 1;
    const charged = "rgba(207,135,232,0.55)";
    const empty = "rgba(8,10,24,0.5)";
    const pct = Math.round(frac * 100);
    this.ultBtn.style.background = ready
      ? charged
      : `linear-gradient(to top, ${charged} ${pct}%, ${empty} ${pct}%)`;
    this.ultBtn.classList.toggle("ready", ready);
  }

  /** Draw the dynamic move joystick where the player's left thumb is. */
  private drawJoystick(): void {
    if (!this.isTouch || !this.input.joyActive) return;
    const ctx = this.renderer.ctx;
    const range = 60;
    const bx = this.input.joyBaseX;
    const by = this.input.joyBaseY;
    let dx = this.input.joyDX;
    let dy = this.input.joyDY;
    const m = Math.hypot(dx, dy);
    if (m > range) {
      dx = (dx / m) * range;
      dy = (dy / m) * range;
    }
    ctx.save();
    ctx.strokeStyle = "#80a0ff";
    ctx.shadowColor = "#80a0ff";
    ctx.shadowBlur = 10;
    ctx.lineWidth = 3;
    ctx.globalAlpha = 0.45;
    ctx.beginPath();
    ctx.arc(bx, by, range, 0, TAU);
    ctx.stroke();
    ctx.globalAlpha = 0.7;
    ctx.fillStyle = "#80a0ff";
    ctx.beginPath();
    ctx.arc(bx + dx, by + dy, 26, 0, TAU);
    ctx.fill();
    ctx.restore();
  }

  private render(alpha: number): void {
    const dt = 1 / 60;
    this.renderClock += dt;
    const s = this.state;
    const ctx = this.renderer.ctx;

    this.renderer.render(s, alpha, dt);

    const w = this.renderer.viewW;
    const h = this.renderer.viewH;

    if (s.phase !== "title" && s.phase !== "lab") {
      drawHud(ctx, s, w, h);
      drawUpgrades(ctx, s);
      drawBossBar(ctx, s, w);
      // Desktop gets a canvas SURGE dial bottom-right (phones use the HTML button).
      if (!this.isTouch) drawSurgeDial(ctx, s, w, h);
    }

    // Show the on-screen control buttons only during active play.
    if (this.isTouch) {
      document.body.classList.toggle("in-play", s.phase === "playing");
      this.updateUltButton(s);
    }
    if (s.phase === "playing") this.drawJoystick();

    switch (s.phase) {
      case "title":
        drawTitle(ctx, w, h, this.renderClock, this.settings, this.meta);
        break;
      case "lab":
        drawLab(ctx, this.meta, w, h, this.labSel);
        break;
      case "levelup":
        drawLevelUp(ctx, s, w, h, this.renderClock);
        break;
      case "paused":
        drawPause(ctx, w, h);
        break;
      case "gameover":
        drawGameOver(ctx, s, w, h, this.renderClock, this.leaderboard);
        break;
      case "stageintro":
        drawStageIntro(ctx, s.stageIntroIndex, w, h, this.renderClock);
        break;
    }
  }
}

function inRect(p: { x: number; y: number }, r: { x: number; y: number; w: number; h: number }): boolean {
  return p.x >= r.x && p.x <= r.x + r.w && p.y >= r.y && p.y <= r.y + r.h;
}

function pickSeed(): number {
  const params = new URLSearchParams(location.search);
  const raw = params.get("seed");
  if (raw) {
    const n = Number(raw);
    return Number.isFinite(n) ? n >>> 0 : seedFromString(raw);
  }
  // Time-based seed for casual runs (determinism still holds within a run).
  return new Rng((performance.now() * 1000) | 0).int(1, 2 ** 31);
}
